import { Helmet } from 'react-helmet';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import config, { getRoute } from 'Config';
import appendLangParam from 'Lib/utils/appendLangParam';

import { DEFAULT_LANGUAGE } from 'Store/internationalization/constants';
import { getCurrentPath } from 'Store/routing/selectors';
import { getUser, getUsername } from 'Store/webProfile/selectors';

import { getIntlAlternateLinks } from './utils';

const WebProfileMeta = () => {
  const username = useSelector(getUsername);
  const user = useSelector(getUser);
  const currentPath = useSelector(getCurrentPath);
  const { formatMessage, locale } = useIntl();

  if (!username) {
    return null;
  }

  const webProfilePath = getRoute('webProfile', { username }, { withPrefix: false });
  if (currentPath && currentPath !== webProfilePath) {
    return null;
  }

  const profileURL = `${config.sharedUrl}/@${username}`;
  const localizedProfileURL =
    locale === DEFAULT_LANGUAGE ? profileURL : appendLangParam(profileURL, locale);

  const helmetProps: React.ComponentProps<typeof Helmet> = {
    link: [
      ...getIntlAlternateLinks(profileURL, false),
      { rel: 'canonical', href: localizedProfileURL },
    ],
  };

  // Adding additional Helmet props if rendering a valid user
  if (user) {
    const { name, photos } = user;
    const photo = photos?.[0]?.processedFiles?.[0];
    const { url: photoUrl, width = '', height = '' } = photo || {};

    const siteName = formatMessage({ id: 'tinder' });
    const nameAndUsername = `${name} (@${username})`;
    const title = `${nameAndUsername} | ${siteName}`;
    const appURL = `tinder://u/${username}`;
    const description = formatMessage(
      { id: 'WebProfile.description' },
      { userName: nameAndUsername }
    );

    helmetProps.title = title;

    helmetProps.meta = [
      { name: 'description', content: description },

      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:url', content: localizedProfileURL },
      { property: 'og:image', content: photoUrl },
      { property: 'og:image:width', content: `${width}` },
      { property: 'og:image:height', content: `${height}` },
      { property: 'og:type', content: 'profile' },
      { property: 'profile:first_name', content: name },
      { property: 'profile:username', content: username },

      { name: 'twitter:title', content: title },
      { name: 'twitter:image', content: photoUrl },
      { name: 'twitter:app:url:iphone', content: appURL },
      { name: 'twitter:app:url:googleplay', content: appURL },
      { name: 'twitter:description', content: description },

      { property: 'al:ios:url', content: appURL },
      { property: 'al:ios:app_store_id', content: '547702041' },
      { property: 'al:ios:app_name', content: siteName },
      { property: 'al:web:url', content: localizedProfileURL },
      { property: 'al:web:should_fallback', content: 'false' },
    ];

    helmetProps.children = (
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org/',
          '@type': 'Person',
          name,
          image: photo,
          alternateName: username,
          mainEntityOfPage: {
            '@type': 'ProfilePage',
            '@id': profileURL,
          },
        })}
      </script>
    );
  }

  return <Helmet {...helmetProps} />;
};

export default WebProfileMeta;
